/**
 * Agent Writes Tab — Phase 5 of dashboard NeuralGraph cutover.
 *
 * Reverse-chronological ledger of every agent run from `execution_log`,
 * cross-referenced to NeuralGraph episode writes.
 *
 * Backed by `GET /api/v1/neuralgraph/writes`.
 */
import React, { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  ArrowPathIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  CurrencyDollarIcon,
  CpuChipIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import Card from '@/components/common/Card'
import Button from '@/components/common/Button'
import Badge from '@/components/common/Badge'
import { apiClient } from '@/services/api'
import clsx from 'clsx'

type WriteRow = {
  timestamp?: string
  agent_name: string
  request_id: string
  layers_written: string[]
  verification_verdict?: string
  duration_ms?: number
  total_tokens?: number
  total_cost_usd?: number
  success: boolean
  ues_extraction_id?: string
  orchestration_id?: string
}

type DateWindow = '1h' | '24h' | '7d' | 'all'

function relTime(iso?: string): string {
  if (!iso) return '—'
  try {
    const ms = Date.now() - new Date(iso).getTime()
    if (ms < 60_000) return `${Math.floor(ms / 1000)}s ago`
    if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ago`
    if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h ago`
    return `${Math.floor(ms / 86_400_000)}d ago`
  } catch { return iso }
}

function windowToISO(w: DateWindow): string | undefined {
  if (w === 'all') return undefined
  const ms = w === '1h' ? 3_600_000 : w === '24h' ? 86_400_000 : 7 * 86_400_000
  return new Date(Date.now() - ms).toISOString()
}

const AgentWritesTab: React.FC = () => {
  const [agentFilter, setAgentFilter] = useState('')
  const [layerFilter, setLayerFilter] = useState('')
  const [dateWindow, setDateWindow] = useState<DateWindow>('24h')
  const [selected, setSelected] = useState<WriteRow | null>(null)

  const writesQ = useQuery({
    queryKey: ['ng-writes', { agentFilter, dateWindow }],
    queryFn: () => apiClient.getNeuralGraphWrites({
      agent_name: agentFilter || undefined,
      since: windowToISO(dateWindow),
      limit: 200,
    }),
    staleTime: 15_000,
  })

  // Distinct agent options for filter (from current page)
  const agentOptions = useMemo(() => {
    const set = new Set<string>()
    writesQ.data?.items.forEach(w => w.agent_name && set.add(w.agent_name))
    return Array.from(set).sort()
  }, [writesQ.data])

  // Apply layer filter client-side (the API doesn't filter on layer)
  const filtered = useMemo(() => {
    const items = writesQ.data?.items || []
    if (!layerFilter) return items
    return items.filter(w => w.layers_written.includes(layerFilter))
  }, [writesQ.data, layerFilter])

  // Aggregates for the strip header
  const aggregate = useMemo(() => {
    const ok = filtered.filter(w => w.success).length
    const failed = filtered.length - ok
    const tokens = filtered.reduce((s, w) => s + (w.total_tokens || 0), 0)
    const cost = filtered.reduce((s, w) => s + (w.total_cost_usd || 0), 0)
    const dur = filtered.reduce((s, w) => s + (w.duration_ms || 0), 0)
    const avg = filtered.length > 0 ? dur / filtered.length : 0
    return { ok, failed, tokens, cost, avg }
  }, [filtered])

  const handleRefresh = () => writesQ.refetch()

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Agent Writes</h2>
          <p className="text-xs text-muted-foreground">
            Every agent execution that produced a NeuralGraph episode (sourced from execution_log).
          </p>
        </div>
        <Button variant="secondary" size="sm" onClick={handleRefresh}>
          <ArrowPathIcon className={clsx('w-4 h-4 mr-1', writesQ.isFetching && 'animate-spin')} />
          Refresh
        </Button>
      </div>

      {/* Filters + aggregate strip */}
      <Card className="p-3">
        <div className="flex items-end gap-3 flex-wrap">
          <div className="w-44">
            <label className="text-[10px] uppercase tracking-wide text-muted-foreground block mb-1">Agent</label>
            <select
              value={agentFilter}
              onChange={e => setAgentFilter(e.target.value)}
              className="w-full text-sm px-2 py-1.5 rounded-md border border-border bg-card"
            >
              <option value="">all agents</option>
              {agentOptions.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
          <div className="w-32">
            <label className="text-[10px] uppercase tracking-wide text-muted-foreground block mb-1">Layer</label>
            <select
              value={layerFilter}
              onChange={e => setLayerFilter(e.target.value)}
              className="w-full text-sm px-2 py-1.5 rounded-md border border-border bg-card"
            >
              <option value="">all layers</option>
              <option value="episodic">episodic</option>
              <option value="semantic">semantic</option>
              <option value="metamemory">metamemory</option>
            </select>
          </div>
          <div className="w-28">
            <label className="text-[10px] uppercase tracking-wide text-muted-foreground block mb-1">Window</label>
            <select
              value={dateWindow}
              onChange={e => setDateWindow(e.target.value as DateWindow)}
              className="w-full text-sm px-2 py-1.5 rounded-md border border-border bg-card"
            >
              <option value="1h">last 1h</option>
              <option value="24h">last 24h</option>
              <option value="7d">last 7d</option>
              <option value="all">all time</option>
            </select>
          </div>
          {(agentFilter || layerFilter) && (
            <button
              onClick={() => { setAgentFilter(''); setLayerFilter('') }}
              className="text-xs text-muted-foreground hover:text-foreground py-1.5"
            >
              clear
            </button>
          )}

          <div className="ml-auto flex items-center gap-4 text-xs text-muted-foreground">
            <span><span className="text-emerald-600 font-semibold">{aggregate.ok}</span> ok</span>
            {aggregate.failed > 0 && <span className="text-red-500">{aggregate.failed} failed</span>}
            <span>{aggregate.tokens.toLocaleString()} tokens</span>
            <span>${aggregate.cost.toFixed(4)}</span>
            <span>avg {Math.round(aggregate.avg)}ms</span>
          </div>
        </div>
      </Card>

      {/* Table */}
      <Card className="p-0 overflow-hidden">
        {writesQ.isLoading ? (
          <div className="p-6 text-center text-sm text-muted-foreground">Loading…</div>
        ) : writesQ.isError ? (
          <div className="p-6 text-center text-sm text-red-500">
            Writes query failed: {(writesQ.error as any)?.message || 'unknown'}
          </div>
        ) : filtered.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-muted/40 border-b border-border text-[10px] uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="text-left px-3 py-2 font-medium">When</th>
                  <th className="text-left px-3 py-2 font-medium">Agent</th>
                  <th className="text-left px-3 py-2 font-medium">Status</th>
                  <th className="text-left px-3 py-2 font-medium">Layers</th>
                  <th className="text-right px-3 py-2 font-medium">Duration</th>
                  <th className="text-right px-3 py-2 font-medium">Tokens</th>
                  <th className="text-right px-3 py-2 font-medium">Cost</th>
                  <th className="text-left px-3 py-2 font-medium">request_id</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filtered.map(w => (
                  <tr
                    key={w.request_id}
                    onClick={() => setSelected(w)}
                    className="cursor-pointer hover:bg-muted/30 transition-colors"
                  >
                    <td className="px-3 py-2 text-muted-foreground whitespace-nowrap" title={w.timestamp}>
                      {relTime(w.timestamp)}
                    </td>
                    <td className="px-3 py-2 text-foreground font-medium whitespace-nowrap">
                      {w.agent_name}
                    </td>
                    <td className="px-3 py-2">
                      {w.success ? (
                        <CheckCircleIcon className="w-4 h-4 text-emerald-500" />
                      ) : (
                        <XCircleIcon className="w-4 h-4 text-red-500" />
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex gap-1">
                        {w.layers_written.map(l => (
                          <span key={l} className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-500/15 text-blue-600">
                            {l}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right text-muted-foreground tabular-nums">
                      {w.duration_ms ? `${w.duration_ms}ms` : '—'}
                    </td>
                    <td className="px-3 py-2 text-right text-muted-foreground tabular-nums">
                      {w.total_tokens?.toLocaleString() || '—'}
                    </td>
                    <td className="px-3 py-2 text-right text-muted-foreground tabular-nums">
                      {w.total_cost_usd != null ? `$${w.total_cost_usd.toFixed(4)}` : '—'}
                    </td>
                    <td className="px-3 py-2">
                      <code className="text-[10px] font-mono text-muted-foreground">
                        {w.request_id.slice(0, 8)}…
                      </code>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="px-3 py-2 text-[10px] text-muted-foreground border-t border-border">
              Showing {filtered.length} of {writesQ.data?.total || 0}
              {writesQ.data && writesQ.data.total >= 200 && ' — increase the time window to see more'}
            </div>
          </div>
        ) : (
          <div className="p-6 text-center text-sm text-muted-foreground">
            No writes match the current filters
          </div>
        )}
      </Card>

      {/* Detail modal */}
      {selected && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => setSelected(null)}
        >
          <Card
            className="max-w-2xl w-full max-h-[80vh] overflow-y-auto"
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-semibold text-foreground">Execution detail</h3>
              <button onClick={() => setSelected(null)} className="text-muted-foreground hover:text-foreground">
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-3 text-sm">
              <DetailRow label="Agent">
                <span className="font-semibold">{selected.agent_name}</span>
              </DetailRow>
              <DetailRow label="Status">
                {selected.success ? (
                  <Badge variant="success">success</Badge>
                ) : (
                  <Badge variant="error">failed</Badge>
                )}
              </DetailRow>
              <DetailRow label="When">
                <span title={selected.timestamp}>
                  {relTime(selected.timestamp)} <span className="text-xs text-muted-foreground">({selected.timestamp})</span>
                </span>
              </DetailRow>
              <DetailRow label="Layers written">
                <div className="flex gap-1">
                  {selected.layers_written.map(l => (
                    <Badge key={l} variant="primary">{l}</Badge>
                  ))}
                </div>
              </DetailRow>
              {selected.verification_verdict && (
                <DetailRow label="Verification">
                  <Badge variant={selected.verification_verdict === 'PASS' ? 'success' : 'warning'}>
                    {selected.verification_verdict}
                  </Badge>
                </DetailRow>
              )}
              <DetailRow label="Duration" icon={<ClockIcon className="w-3 h-3" />}>
                {selected.duration_ms ? `${selected.duration_ms} ms` : '—'}
              </DetailRow>
              <DetailRow label="Tokens" icon={<CpuChipIcon className="w-3 h-3" />}>
                {selected.total_tokens?.toLocaleString() || '—'}
              </DetailRow>
              <DetailRow label="Cost" icon={<CurrencyDollarIcon className="w-3 h-3" />}>
                {selected.total_cost_usd != null ? `$${selected.total_cost_usd.toFixed(6)}` : '—'}
              </DetailRow>
              <DetailRow label="request_id">
                <code className="text-xs font-mono break-all">{selected.request_id}</code>
              </DetailRow>
              {selected.orchestration_id && (
                <DetailRow label="orchestration_id">
                  <code className="text-xs font-mono break-all">{selected.orchestration_id}</code>
                </DetailRow>
              )}
              {selected.ues_extraction_id && (
                <DetailRow label="ues_extraction_id">
                  <code className="text-xs font-mono break-all">{selected.ues_extraction_id}</code>
                </DetailRow>
              )}

              <div className="pt-3 mt-3 border-t border-border text-xs text-muted-foreground">
                For the full prompt, output, and audit chain, jump to the
                {' '}
                <a href={`/operations?request_id=${encodeURIComponent(selected.request_id)}`}
                   className="text-primary hover:underline">
                  Operations tab →
                </a>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  )
}

function DetailRow({ label, icon, children }: { label: string; icon?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-3 text-sm">
      <span className="text-muted-foreground inline-flex items-center gap-1 shrink-0">
        {icon}{label}
      </span>
      <div className="text-foreground text-right">{children}</div>
    </div>
  )
}

export default AgentWritesTab
